import { PropertiesStore } from '@hortplus/properties-react';
import { Formik } from 'formik';
import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import LoadingOverlay from '../../LoadingOverlay';
import { useStores } from '../../stores/useStores';
import { dateFormatRegex } from '../../utils';
import TrappingZoneSprayForm from '../TrappingZoneSprayForm';
import { initialValues, submitForm } from './utils';

function AddTrappingZoneSprayModal(props) {
  const [show, setShow] = useState(false);
  const { traplogStore } = useStores();

  const databaseYear = traplogStore.selectedDatabaseYear() - 1;

  if (!props.addMany && !props.trappingZone) {
    throw Error(
      'When adding a single trapping zone spray, this component needs a "trappingZone".'
    );
  }

  const hide = () => {
    setShow(false);
  };

  return (
    <React.Fragment>
      <span onClick={() => setShow(true)}>{props.children}</span>
      <Modal backdrop='static' show={show} onHide={() => hide()}>
        <Modal.Header closeButton>
          <Modal.Title>
            Add Spray Application{' '}
            {!props.addMany && `(${props.trappingZone.name})`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues({
              ...props,
              initialTrappingZone: traplogStore.selectedTrappingZone
            })}
            validate={(values) => {
              const errors = {};
              const appliedYear = moment(values.applied_date).year();

              if (!values.sprayProduct) errors.sprayProduct = 'Required';
              if (
                !props.addMany &&
                !values.trapping_zone_id &&
                values.trapping_zone_id !== 0
              )
                errors.trapping_zone_id = 'Required';
              if (props.addMany && values.trapping_zones?.length === 0) {
                errors.trapping_zones = 'Please select a Production Site.';
              }
              if (!values.applied_date) {
                errors.applied_date = 'Required';
              } else if (appliedYear < databaseYear) {
                errors.applied_date = `The year must be ${databaseYear} or later.`;
              } else if (appliedYear > databaseYear + 1) {
                errors.applied_date = `The year cannot be larger than ${
                  databaseYear + 1
                }.`;
              } else if (!dateFormatRegex.test(values.applied_date)) {
                errors.applied_date =
                  'Invalid date. Please use the format "YYYY-MM-DD", and be careful of leap years.';
              }
              if (!values.coverage_days) errors.coverage_days = 'Required';
              // External errors. Errors not related to specific input values.
              // Biofix validation
              errors.external = {};
              if (values.biofix) {
                const selectedTrappingZones = values.trapping_zones || [
                  {
                    value: props.trappingZone.id,
                    label: props.trappingZone.name
                  }
                ];
                // Find zones that already have biofix
                const zonesWithBiofix = Object.keys(props.trappingResults)
                  .filter((id) => {
                    const selectedZone = selectedTrappingZones.find(
                      (z) => z.value === Number(id)
                    );
                    return (
                      selectedZone &&
                      props.trappingResults[id].validation?.biofix?.status ===
                        'ok'
                    );
                  })
                  .map(
                    (id) =>
                      selectedTrappingZones.find((z) => z.value === Number(id))
                        ?.label
                  )
                  .filter(Boolean);
                // Attach biofix validation errors if they exist.
                if (zonesWithBiofix.length > 0) {
                  const verb = zonesWithBiofix.length === 1 ? 'has' : 'have';
                  errors.external.existingBiofix = `${zonesWithBiofix.join(
                    ', '
                  )} already ${verb} a biofix spray.`;
                }
              }
              return errors;
            }}
            onSubmit={(values, actions) => {
              const newValues = {
                ...values,
                trapping_zones: props.addMany
                  ? values.trapping_zones.map((tz) => tz.value)
                  : []
              };
              submitForm({
                addMany: props.addMany,
                values: newValues,
                actions,
                traplogStore,
                propertiesStore: props.propertiesStore
              });
            }}
          >
            {(fProps) => (
              <Fragment>
                <TrappingZoneSprayForm
                  {...fProps}
                  selectTrappingZones={props.addMany}
                  coverageDays={props.coverageDays}
                />
                <Button
                  varient='primary'
                  type='button'
                  onClick={fProps.handleSubmit}
                >
                  Submit
                </Button>
                {fProps.isSubmitting ? <LoadingOverlay /> : null}
              </Fragment>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
}

AddTrappingZoneSprayModal.propTypes = {
  addMany: PropTypes.bool,
  trappingZone: PropTypes.object,
  trappingResults: PropTypes.array.isRequired,
  coverageDays: PropTypes.bool,
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired
};

export default observer(AddTrappingZoneSprayModal);
